import React, { useEffect, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { useMediaQuery } from "@mui/material";

import { Link as RouterLink, Outlet, useLocation } from "react-router-dom";
import { charityName } from "../../util/Constants";
import { cutText } from "../../themes/CutText";
import Socials from "./Socials";
import ScrollToTop from "./ScrollToTop";
import PageMetadata from "./PageMetadata";

const drawerWidth = 240;

const Main = styled("div", { shouldForwardProp: (prop) => !["open", "isMobile"].includes(prop) })(
  ({ theme, isMobile }) => ({
    flexGrow: 1,
    padding: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginRight: 0,
    position: "relative",
    variants: [
      {
        props: ({ open }) => open,
        style: {
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginRight: 0,
        },
      },
    ],
  })
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => !["open", "isMobile"].includes(prop),
})(({ theme, isMobile }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        width: isMobile ? "100%" : `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(["margin", "width"], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginRight: isMobile ? 0 : drawerWidth,
      },
    },
  ],
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-start",
}));

export default function PersistentDrawerRight() {
  const theme = useTheme();
  const location = useLocation();
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [open, setOpen] = React.useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <PageMetadata />
      <AppBar
        position="fixed"
        open={false}
        isMobile={isMobile}
        elevation={0}
        sx={{
          bgcolor: "rgba(255,255,255,0.92)",
          color: theme.palette.primary.main,
          borderBottom: "1px solid rgba(6, 57, 112, 0.1)",
          backdropFilter: "blur(16px)",
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 72 } }}>
          <Box
            component={RouterLink}
            to="/"
            aria-label="Cyrenaeus home"
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            <Typography
              variant={isMobile ? "h5" : "h4"}
              noWrap
              sx={cutText("/images/colour.jpeg")}
              component="div"
            >
              {charityName}
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to="/donate"
            variant="contained"
            startIcon={<FavoriteIcon />}
            sx={{
              bgcolor: theme.palette.donate.primary,
              "&:hover": {
                backgroundColor: theme.palette.donate.secondary,
                transform: "translateY(-1px)",
              },
              color: theme.palette.common.black,
              marginRight: 2,
              px: { xs: 1.5, sm: 2.5 },
              transition: "all 180ms ease",
              "& .MuiButton-startIcon": {
                display: { xs: "none", sm: "inherit" },
              },
            }}
          >
            Donate
          </Button>
          <IconButton
            aria-label="Open navigation"
            aria-expanded={open}
            aria-controls={open ? "site-navigation" : undefined}
            edge="end"
            onClick={handleDrawerOpen}
            sx={[
              {
                color: theme.palette.primary.main,
                border: "1px solid rgba(6, 57, 112, 0.16)",
                transition: "all 180ms ease",
                "&:hover": {
                  bgcolor: "rgba(6, 57, 112, 0.08)",
                  transform: "translateY(-1px)",
                },
              },
              open && { display: "none" },
            ]}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Main
        open={false}
        isMobile={isMobile}
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: viewportHeight,
        }}
      >
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <DrawerHeader />
          <ScrollToTop />
          <Box component="main" id="main-content" tabIndex={-1} sx={{ flex: 1, display: "flex", flexDirection: "column", scrollMarginTop: "80px" }}>
            <Outlet />
          </Box>
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <Socials />
        </Box>
      </Main>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            borderLeft: "1px solid rgba(6, 57, 112, 0.12)",
            background:
              "linear-gradient(180deg, #ffffff 0%, #f7f9fc 58%, #edf4fb 100%)",
          },
        }}
        variant="temporary"
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        slotProps={{ paper: { id: "site-navigation", "aria-label": "Site navigation" } }}
      >
        <Box>
          <DrawerHeader>
            <IconButton
              aria-label="Close navigation"
              onClick={handleDrawerClose}
              sx={{ color: theme.palette.primary.main }}
            >
              {theme.direction === "rtl" ? (
                <ChevronLeftIcon />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <Box component="nav" aria-label="Main navigation"><List>
            {[
              { title: "Home", link: "/" },
              { title: "About Us", link: "/about" },
              { title: "Who We Are", link: "/who-we-are" },
              { title: "Partners", link: "/partners" },
              { title: "Missions", link: "/missions" },
              { title: "News", link: "/news" },
              {
                title: "Benefactors",
                link: "/benefactors",
              },
              { title: "Donate", link: "/donate" },
            ].map((x, index) => (
              <ListItem key={index} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={x.link}
                  aria-current={location.pathname === x.link ? "page" : undefined}
                  onClick={() => {
                    setOpen(false);
                  }}
                  selected={location.pathname === x.link}
                  sx={{
                    mx: 1,
                    my: 0.5,
                    borderRadius: 2,
                    transition: "all 180ms ease",
                    "&.Mui-selected": {
                      bgcolor: "rgba(6, 57, 112, 0.1)",
                      color: theme.palette.primary.main,
                    },
                    "&:hover": {
                      bgcolor: "rgba(6, 57, 112, 0.08)",
                      transform: "translateX(-2px)",
                    },
                  }}
                >
                  <ListItemText
                    primary={x.title.toUpperCase()}
                    primaryTypographyProps={{
                      fontWeight: 900,
                      fontSize: 13,
                      letterSpacing: 0.8,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List></Box>
        </Box>
        <a
          href="https://register-of-charities.charitycommission.gov.uk/en/charity-search/-/charity-details/5211178/charity-overview"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary">
              UK registered charity
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Charity number: 1202741
            </Typography>
          </Box>
        </a>
      </Drawer>
    </Box>
  );
}
